import { makeAutoObservable, runInAction } from 'mobx';
import { session } from 'auth/session';
import * as api from 'auth/api';
import { authError } from 'auth/errors';
import tenantDetails, { ITenantDetailsResponse } from 'requests/auth/tenantDetails';

export enum EAuthStage {
  IDLE = 'idle',
  INITIALIZING = 'initializing',
  CHECK_EMAIL = 'check_email',
  PASSWORD_CHALLENGE = 'password_challenge',
  TWO_FA_CHALLENGE = 'two_fa_challenge',
  AUTHENTICATED = 'authenticated',
  SERVICE_ERROR = 'service_error'
}
const blankTenant = (): ITenantDetailsResponse => ({
  email: '',
  scopes: [],
  api_consumer_profile: { name: '' },
  api_consumer: {
    id: '',
    app_key: '',
    client_id: '',
    inserted_at: '',
    password: '',
    status: '',
    type: '',
    updated_at: '',
    username: ''
  }
});

export class AuthStoreImpl {
  accessToken = '';

  tokenExpiresAt = 0;

  authStage = EAuthStage.IDLE;

  initialized = false;

  loggedOut = true;

  signinEmail = '';

  mfaMethod: 'email' | 'totp' = 'email';

  challengeExpiresAt = 0;

  resendAt = 0;

  Auth: IAuth = { tenant: null };

  tenantDetails = blankTenant();

  isLoading = {
    login: false,
    logout: false,
    verify: false,
    refresh: false,
    checkEmail: false,
    changePassword: false,
    resend: false
  };

  errors = {
    login: '',
    logout: '',
    verify: '',
    refresh: '',
    checkEmail: '',
    changePassword: '',
    initialize: ''
  };

  passwordChanged = false;

  logoutAllRequested = false;

  retryAfter = 0;

  private initialization?: Promise<void>;

  private subscribed = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  async initialize(retry = false) {
    if (this.initialization) return this.initialization;
    if (this.initialized && !retry) return undefined;
    this.authStage = EAuthStage.INITIALIZING;
    this.errors.initialize = '';
    this.initialization = this.restore().finally(() => {
      runInAction(() => {
        this.initialization = undefined;
        this.initialized = true;
      });
    });
    return this.initialization;
  }

  private async restore() {
    try {
      session.start();
      if (!this.subscribed) {
        this.subscribed = true;
        session.subscribe((event, error) => {
          runInAction(() => {
            if (event === 'cleared' || event === 'replaced') {
              const hadAccount = !this.loggedOut;
              this.sessionCleanup();
              if (hadAccount) window.location.replace('/auth/login');
              else if (event === 'replaced') {
                this.initialized = false;
                this.initialize(true);
              }
            } else if (event === 'challenge') {
              if (this.loggedOut) this.syncChallenge();
            } else if (event === 'token') {
              this.accessToken = session.accessToken;
              this.tokenExpiresAt = session.accessExpiresAt;
              this.errors.refresh = '';
            } else if (event === 'error') this.errors.refresh = error.message;
          });
        });
      }
      if (session.metadata) {
        await session.getAccessToken();
        await this.fetchTenantDetails();
      } else this.syncChallenge();
    } catch (error) {
      runInAction(() => {
        const failure = authError(error);
        if (failure.status === 401) {
          this.syncChallenge();
          this.errors.login = failure.message;
        } else {
          this.errors.initialize = failure.message;
          this.authStage = EAuthStage.SERVICE_ERROR;
        }
      });
    }
  }

  private requestFailure(error: unknown) {
    const failure = authError(error);
    if (failure.status === 429) this.retryAfter = Date.now() + 60000;
    return failure.message;
  }

  syncChallenge() {
    const { challenge } = session;
    this.signinEmail = challenge?.email || '';
    this.challengeExpiresAt = challenge?.expiresAt || 0;
    this.resendAt = challenge?.resendAt || 0;
    this.mfaMethod = challenge?.method || 'email';
    this.authStage = !challenge
      ? EAuthStage.CHECK_EMAIL
      : challenge.stage === 'password'
      ? EAuthStage.PASSWORD_CHALLENGE
      : EAuthStage.TWO_FA_CHALLENGE;
  }

  startOver() {
    session.abandonChallenge();
    this.errors.login = '';
    this.errors.checkEmail = '';
    this.errors.verify = '';
    this.syncChallenge();
  }

  async checkEmail(email: string) {
    if (this.isLoading.checkEmail || Date.now() < this.retryAfter) return;
    this.isLoading.checkEmail = true;
    this.errors.checkEmail = '';
    this.errors.login = '';
    this.errors.verify = '';
    try {
      await api.checkEmail(email);
      runInAction(this.syncChallenge);
    } catch (error) {
      runInAction(() => {
        this.errors.checkEmail = this.requestFailure(error);
      });
    } finally {
      runInAction(() => {
        this.isLoading.checkEmail = false;
      });
    }
  }

  async login(password: string) {
    if (this.isLoading.login || Date.now() < this.retryAfter) return;
    this.isLoading.login = true;
    this.errors.login = '';
    try {
      await api.signIn(password);
      runInAction(this.syncChallenge);
    } catch (error) {
      runInAction(() => {
        this.errors.login = this.requestFailure(error);
        if (!session.challenge) this.syncChallenge();
      });
    } finally {
      runInAction(() => {
        this.isLoading.login = false;
      });
    }
  }

  async verify({ code }: { code: string }) {
    if (this.isLoading.verify || Date.now() < this.retryAfter) return;
    this.isLoading.verify = true;
    this.errors.verify = '';
    try {
      await api.verifyOtp(code);
      await this.fetchTenantDetails();
    } catch (error) {
      runInAction(() => {
        if (session.metadata) {
          this.authStage = EAuthStage.SERVICE_ERROR;
          this.errors.initialize = authError(error).message;
        } else {
          this.errors.verify = this.requestFailure(error);
          if (!session.challenge) this.syncChallenge();
        }
      });
    } finally {
      runInAction(() => {
        this.isLoading.verify = false;
      });
    }
  }

  async resend() {
    if (this.isLoading.resend || Date.now() < this.retryAfter) return;
    this.isLoading.resend = true;
    this.errors.verify = '';
    try {
      await api.resendOtp();
      runInAction(this.syncChallenge);
    } catch (error) {
      runInAction(() => {
        this.errors.verify = this.requestFailure(error);
      });
    } finally {
      runInAction(() => {
        this.isLoading.resend = false;
      });
    }
  }

  async fetchTenantDetails() {
    const id = session.metadata?.id;
    const { data } = await tenantDetails();
    if (!id || id !== session.metadata?.id) return;
    if (
      !data?.email ||
      !data.api_consumer ||
      !data.api_consumer_profile ||
      !Array.isArray(data.scopes)
    )
      throw new Error('Invalid tenant profile');
    runInAction(() => {
      this.tenantDetails = data;
      this.Auth = {
        tenant: {
          email: data.email,
          companyName: data.api_consumer_profile.name,
          id: data.api_consumer.id,
          scopes: data.scopes,
          companyId: data.api_consumer.client_id
        }
      };
      this.accessToken = session.accessToken;
      this.tokenExpiresAt = session.accessExpiresAt;
      this.loggedOut = false;
      this.authStage = EAuthStage.AUTHENTICATED;
    });
  }

  get getAuthUser() {
    return this.tenantDetails;
  }

  async changePassword({
    old_password,
    new_password
  }: Omit<IChangePassword, 'confirm_new_password'>) {
    if (this.isLoading.changePassword) return;
    this.isLoading.changePassword = true;
    this.errors.changePassword = '';
    try {
      await api.changePassword(old_password, new_password);
      window.location.replace('/auth/login');
    } catch (error) {
      runInAction(() => {
        this.errors.changePassword = authError(error).message;
      });
    } finally {
      runInAction(() => {
        this.isLoading.changePassword = false;
      });
    }
  }

  async logout(all = false) {
    if (this.isLoading.logout) return;
    this.logoutAllRequested = all;
    this.isLoading.logout = true;
    this.errors.logout = '';
    try {
      await api.signOut(all);
      window.location.replace('/auth/login');
    } catch (error) {
      runInAction(() => {
        this.errors.logout = `Server sign-out was not confirmed. ${authError(error).message}`;
      });
    } finally {
      runInAction(() => {
        this.isLoading.logout = false;
      });
    }
  }

  sessionCleanup() {
    this.accessToken = '';
    this.tokenExpiresAt = 0;
    this.loggedOut = true;
    this.Auth = { tenant: null };
    this.tenantDetails = blankTenant();
    this.authStage = EAuthStage.CHECK_EMAIL;
  }
}

const AuthStore = new AuthStoreImpl();
export default AuthStore;
